import React, { FC, useState } from 'react';
import { Box, RedButton } from '~Styles';
import styled from '@emotion/styled';
import { useTableUniverse } from '~Components/TableUniverse';
import Heading from '~ComponentsImproved/Typography/Heading';
import useI18n from '~Translations';
import { gql, useQuery } from '@apollo/client';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useCurrency, useDispatch, useLogger, useStaffCan } from '~Hooks';
import { handyBreakpoint } from '~Styles/breakpoints';
import Modal from '~ComponentsImproved/Modal';
import { useTableApi } from '~Components/TableUniverse';
import OngoingChequePayment from './OngoingChequePayment';
import { addToast } from '~/actions';
import { BlackButton } from '~Styles';
import { ModalContent } from '~Styles/modal';
import WarningIcon from '~Assets/icons/warning.svg';
import Icon from '~ComponentsImproved/Icon';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  max-width: 600px;
  margin-bottom: 20px; // this because we ignore padding bottom in the parent to enable scroll to bottom for the left section
  border: 1px solid var(--colorBlueGray700);
  padding: 16px;
  width: 100%;
  margin: 0 auto;

  ${handyBreakpoint} {
    border: none;
    background: transparent;
  }
`;

const Totals = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  margin-top: 16px;

  ${handyBreakpoint} {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }
`;

const ButtonsWrapper = styled(Box)`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const GET_TABLE_NAME = gql`
  query getTable($tableId: ID!) {
    Table(id: $tableId) {
      code
    }
  }
`;

interface Props {
  chequeId: string;
}

const OngoingCheque: FC<Props> = ({ chequeId }) => {
  const universe = useTableUniverse();
  const { i18n } = useI18n();
  const currency = useCurrency();
  const logger = useLogger('history-v2');
  const staffCan = useStaffCan();
  const { removePayment } = useTableApi();
  const dispatch = useDispatch();

  const [voidingPaymentId, setVoidingPaymentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const cheque = universe.cheques.find((c) => c.id === chequeId);
  const tableNameQuery = useQuery(GET_TABLE_NAME, {
    variables: { tableId: cheque?.tableId || '' },
    skip: !cheque?.tableId,
  });
  const tableName = tableNameQuery.data?.Table?.code || '';
  const payments = universe.payments.filter((p) => p.chequeId === chequeId);

  // Handler for voiding a payment
  const handleVoidPayment = async (paymentId: string) => {
    setLoading(true);
    logger.info('Voiding payment...', { paymentId, chequeId });

    try {
      removePayment(paymentId);
      logger.info('Payment voided successfully', { paymentId });
      // Show success toast
      dispatch(addToast(i18n('History.VoidSuccess') || 'Payment voided successfully', true));
    } catch (error) {
      logger.error('Failed to void payment', { paymentId, errorMessage: String(error) });
      // Show error toast
      dispatch(addToast(i18n('History.VoidFailed') || 'Failed to void payment', true));
    } finally {
      setLoading(false);
      setVoidingPaymentId(null);
    }
  };

  // Modal buttons - must be before early return
  const cancelButton = useButton({
    onClick: () => setVoidingPaymentId(null),
  });

  const confirmButton = useButton({
    onClick: () => {
      if (voidingPaymentId) {
        handleVoidPayment(voidingPaymentId);
      }
    },
    disabled: loading,
  });

  if (tableNameQuery.loading || !cheque) {
    return null;
  }

  const chequeTotal = cheque.amount - (cheque.discount || 0);

  return (
    <Wrapper>
      <Box display="flex" gap="small" justifyContent="space-between" alignItems="center">
        <Heading level={2} color="var(--colorWhite)">
          {`${i18n('History.Cheque')} #${cheque?.name ?? cheque?.id.toUpperCase().slice(0, 4)}`}
        </Heading>
        <Heading level={4} color="var(--colorWhite)">
          {`${i18n('History.Table')} ${tableName}`}
        </Heading>
      </Box>
      <Totals>
        <Paragraph level={4} color="var(--colorBlueGray200)">
          {i18n('History.TotalAmount')}: {currency.format(chequeTotal)}
        </Paragraph>
        <Paragraph level={4} color="var(--colorBlueGray200)">
          {i18n('History.ChequeAmount.Paid')}: {currency.format(cheque?.paidAmount || 0)}
        </Paragraph>
        <Paragraph level={4} color="var(--colorBlueGray200)">
          {i18n('History.ChequeAmount.Remaining')}:{' '}
          {currency.format(chequeTotal - (cheque?.paidAmount || 0))}
        </Paragraph>
      </Totals>

      <Box marginTop="large">
        <Heading level={3} color="var(--colorWhite)" bottom={8}>
          {i18n('History.Payments')}
        </Heading>

        {payments.map((payment) => (
          <OngoingChequePayment
            key={payment.id}
            payment={payment}
            loading={loading}
            staffCan={staffCan}
            i18n={i18n}
            currency={currency}
            onVoidClick={setVoidingPaymentId}
            chequeId={chequeId}
          />
        ))}
      </Box>

      {/* Void confirmation modal with explanation */}
      <Modal isShowing={voidingPaymentId !== null} hide={() => setVoidingPaymentId(null)}>
        <ModalContent>
          <Icon src={WarningIcon} height="48px" width="48px" />
          <Heading level={3} color="var(--colorWhite)" bottom={16} top={16}>
            {i18n('History.VoidPayment')}
          </Heading>
          <Paragraph level={3} color="var(--colorBlueGray200)" bottom={8}>
            {i18n('History.VoidExplanation')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorUIRed)">
            {i18n('History.ActionIrreversible')}
          </Paragraph>
          <ButtonsWrapper>
            <BlackButton fluid {...cancelButton}>
              {i18n('History.Cancel')}
            </BlackButton>
            <RedButton fluid {...confirmButton}>
              {loading ? i18n('History.Voiding') : i18n('History.Confirm')}
            </RedButton>
          </ButtonsWrapper>
        </ModalContent>
      </Modal>
    </Wrapper>
  );
};

export default OngoingCheque;
